import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// 新しいパスワード用コンポーネント
interface GradientBorderPasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  width?: number;
  showPassword: boolean;
  onTogglePassword: () => void;
  autocomplete?: string;
}

const GradientBorderPasswordField: React.FC<
  GradientBorderPasswordFieldProps
> = ({
  value,
  onChange,
  label,
  width = 390,
  showPassword,
  onTogglePassword,
  autocomplete,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const DynamicSVGBorder = () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 670 104"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <defs>
        <linearGradient
          id={`borderGradient-password-${isFocused ? "focused" : "normal"}`}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop
            offset="0%"
            style={{
              stopColor: isFocused ? "#a8a8d4" : "#a8a8d4", // フォーカス時は元の色、非フォーカス時は灰色
            }}
          />
          <stop
            offset="100%"
            style={{
              stopColor: isFocused ? "#e3ff7f" : "#e3ff7f", // フォーカス時は元の色、非フォーカス時は灰色
            }}
          />
        </linearGradient>
      </defs>
      <rect
        x="1"
        y="1"
        width="658"
        height="100"
        fill={"#ffffff"} // 背景色を常に白に統一
        stroke={`url(#borderGradient-password-${
          isFocused ? "focused" : "normal"
        })`}
        strokeWidth="6"
        rx="22"
      />
    </svg>
  );

  return (
    <Box sx={{ position: "relative", width, height: 60 }}>
      <DynamicSVGBorder />
      <TextField
        type={showPassword ? "text" : "password"} // 標準のtype属性を使用
        value={value}
        onChange={(e) => onChange(e.target.value)} // シンプルなonChange
        placeholder={label}
        size="small"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        inputProps={{
          autoComplete: autocomplete, // autocompleteプロパティを適用
          spellCheck: false, // スペルチェック無効化
        }}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
          "& .MuiOutlinedInput-root": {
            height: "100%",
            padding: "12px", // Add padding to contain the autofill highlight
            "& fieldset": {
              border: "none",
            },
            "&:hover fieldset": {
              border: "none",
            },
            "&.Mui-focused fieldset": {
              border: "none",
            },
            backgroundColor: "transparent",
            borderRadius: "4px",
          },
          "& .MuiInputBase-input": {
            padding: "8px 40px 8px 12px", // 右側にボタン用のスペースを確保
            fontSize: "16px",
            // 万が一オートフィルが動作した場合の最小限の対応
            "&:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
              WebkitTextFillColor: "#000 !important",
              backgroundColor: "transparent !important",
            },
            "&:-webkit-autofill:hover": {
              WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
              WebkitTextFillColor: "#000 !important",
              backgroundColor: "transparent !important",
            },
            "&:-webkit-autofill:focus": {
              WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
              WebkitTextFillColor: "#000 !important",
              backgroundColor: "transparent !important",
            },
            "&:-webkit-autofill:active": {
              WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
              WebkitTextFillColor: "#000 !important",
              backgroundColor: "transparent !important",
            },
            "&::placeholder": {
              color: isFocused ? "#999" : "#bbb", // プレースホルダーの色も変更
              opacity: 1,
            },
          },
        }}
      />
      <IconButton
        aria-label="toggle password visibility"
        onClick={onTogglePassword}
        edge="end"
        style={{
          position: "absolute",
          right: "24px",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
        }}
      >
        {showPassword ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    </Box>
  );
};

export default GradientBorderPasswordField;